import { useEffect, useState } from "react";
import { Link } from "react-router";
import * as api from "../../lib/api.ts";

/**
 * Первые шаги в пустом кабинете: цепочка разделов.
 *
 * Зачем вообще. Каждый экран по отдельности объясняет себя сам (пустое
 * состояние всюду говорит, что делать дальше), а вот ПОРЯДОК разделов из
 * сайдбара не выводится: почему «Материалы» и «Заготовки» это два пункта, а не
 * один, видно только после того, как соберёшь первый слайд. Человек догадается
 * за два-три занятия и всё это время будет считать интерфейс переусложнённым.
 *
 * ⚠️ **Это не тур и не мастер, и разница принципиальная.** У тура есть
 * состояние («шаг 4 из 7»), а состояние ломается, и ломается оно поверх живого
 * экрана. Тур показывается ровно один раз, в момент, когда подсвечивать нечего,
 * и забывается к той неделе, когда пригодился бы. Здесь же обычный текст на
 * обычном экране: он ничего не перекрывает, его не нужно закрывать, и он
 * исчезает сам, когда перестаёт быть правдой.
 *
 * ⚠️ **Ученику этот текст не показывается никогда.** Он входит по ссылке, без
 * аккаунта, посреди первого занятия: первого логина у него нет вовсе, и
 * единственная допустимая документация для него это отсутствие нужды в ней.
 *
 * ⚠️ **Разметка ОДНА на два вида, разводит их только CSS, и это осознанно.**
 * Широкий экран показывает подсказку колонкой справа от сетки, узкий сворачивает
 * её в строку-цепочку над сеткой. Второго компонента и второго текста нет
 * намеренно: две копии одних и тех же пяти названий разошлись бы на первой же
 * правке, а разошедшаяся подсказка хуже отсутствующей. Поэтому подробности
 * лежат в отдельных `<span>`, которые узкий вид просто гасит.
 *
 * Чего здесь НЕ делается: подсказка не ложится оверлеем поверх сетки. Карточку
 * нельзя одновременно сделать кликабельной (в ней пять ссылок) и прозрачной для
 * клика по слоту под ней. Прозрачный вариант технически есть, но тогда нажатие
 * на текст молча открывает диалог занятия, а это сюрприз, а не механика.
 */
export function FirstSteps() {
  return (
    <div className="card first-steps">
      <p>
        Кабинет пуст: ни одного ученика.{" "}
        <span className="steps__note">
          Занятие принадлежит паре «преподаватель и ученик», поэтому начинать надо с человека, а
          не с материала.
        </span>
      </p>

      <ul className="steps">
        <li>
          <p className="muted">
            <Link className="textlink" to="/students">
              Ученики
            </Link>
            <span className="steps__note">
              . Заведите ученика и отдайте ему постоянную ссылку: по ней он заходит на все занятия
              с вами, а не на одно. Ссылка показывается один раз.
            </span>
          </p>
        </li>
        <li>
          <p className="muted">
            <Link className="textlink" to="/media">
              Материалы
            </Link>
            <span className="steps__note">
              . Ваше личное хранилище: картинки, PDF, аудио и видео. Файл лежит у нас, а не по
              ссылке на чужой сервис, поэтому аудирование не зависит от того, что сегодня
              замедлено.
            </span>
          </p>
        </li>
        <li>
          <p className="muted">
            <Link className="textlink" to="/plans">
              Заготовки
            </Link>
            <span className="steps__note">
              . Слайды, собранные заранее, из этих самых материалов. Урок получает копию слайдов,
              поэтому правка заготовки прошлые занятия не меняет, а по одной заготовке проводятся
              занятия с разными людьми.
            </span>
          </p>
        </li>
        <li>
          <p className="muted">
            Занятие
            <span className="steps__note">
              . Нажмите на пустое время в календаре слева: разовое либо повторяющееся каждую
              неделю. Ученика при этом можно завести прямо в том же окне.
            </span>
          </p>
        </li>
        <li>
          <p className="muted">
            <Link className="textlink" to="/homework">
              Домашка
            </Link>
            <span className="steps__note">
              . Задаётся после занятия. Задания с правильным ответом проверяются сами, а перевод,
              ответ текстом и фото проверяете вы. Результат виден в карточке ученика.
            </span>
          </p>
        </li>
      </ul>

      <p className="muted steps__tail">
        Обязателен только первый шаг. Урок открывается и на чистой доске, без материалов и без
        заготовки.
      </p>
    </div>
  );
}

/**
 * Пуст ли кабинет. `true` только тогда, когда учеников нет ни одного.
 *
 * ⚠️ **Запрос уходит лениво и не чаще одного раза на заход в раздел.** Флаг
 * нужен ровно в той ветке, где на экране и так пусто, поэтому дёргать список
 * учеников на каждой отрисовке расписания незачем: у работающего преподавателя
 * эта ветка не наступает никогда.
 *
 * ⚠️ **Отказ считается «кабинет не пуст».** Сеть отвалилась это не повод
 * показать человеку с двадцатью учениками текст про первые шаги: ошибка сети
 * уже написана на экране своей строкой.
 *
 * Предикат именно «ноль строк», а не «все отвязаны»: преподаватель, отвязавший
 * последнего ученика, кабинет не обнулил, и слово «пуст» было бы про него
 * неправдой.
 *
 * ⚠️ **`recheck` обязателен, и вот почему.** Ученика заводят не только в
 * разделе «Ученики», но и прямо в диалоге создания занятия. Человек, заведший
 * там ученика и закрывший диалог крестиком, кабинет уже не пустой имеет, а
 * кешированный ответ на экране остался бы прежним, то есть текст «ни одного
 * ученика» стал бы враньём в ту же секунду. Звать после закрытия любого места,
 * где ученик может появиться.
 */
export function useEmptyCabinet(enabled: boolean): { empty: boolean; recheck: () => void } {
  const [empty, setEmpty] = useState<boolean | null>(null);

  useEffect(() => {
    if (!enabled || empty !== null) return;
    let alive = true;
    void api.listStudents().then(
      (res) => {
        if (alive) setEmpty(res.students.length === 0);
      },
      () => {
        if (alive) setEmpty(false);
      },
    );
    return () => {
      alive = false;
    };
  }, [enabled, empty]);

  return {
    empty: empty === true,
    // Сброс в «не знаю»: тот же эффект сходит за ответом заново, но только если
    // ветка пустой недели всё ещё на экране
    recheck: () => setEmpty(null),
  };
}
